import * as THREE from '@phdsoft/three';
import WebGL from 'three/examples/jsm/capabilities/WebGL.js';
import { CSS3DRenderer } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { PerspectiveCamera } from '@phdsoft/three';
import { Injectable } from '@angular/core';
import { Util } from '@phdsoft/common';
import { GraphicsLights } from './graphics-lights';
import { GraphicsMaterials } from './graphics-materials';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { OUTLINE_LAYER_PRIMARY, OUTLINE_LAYER_SECONDARY, SelectionOutlinePass } from './selection-outline-pass';
import { AmbientOcclusionPass } from './ambient-occlusion-pass';
import { CameraMotionTracker, decideAmbientOcclusionFrame } from './ambient-occlusion-gate';
import { AO_LIGHT_MODEL_RADIUS_PER_METRE, AO_LIGHT_MODEL_SCALE, ambientOcclusionWorldRadius } from './ambient-occlusion-radius';
import { lightModelCameraNear } from './light-model-camera-near';
import { ThreeJsCleanup } from '@app/shared/utils/threejs-cleanup.util';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { PlaneUtil } from '../plane';
import { initCSS2DRenderer, createLabel2DObjects, Label2DInfo, updateLabel2DPositions, disposeGroupLinesAndLabels, LabelData } from './labels-utils';
import { ViewModeAnomalyLabelsService } from '@app/template/view-mode-tools/view-mode-anomaly-labels/view-mode-anomaly-labels.service';
import { ViewModeStructureLabelsService } from '@app/template/view-mode-tools/view-mode-structure-labels/view-mode-structure-labels.service';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
// was: import { renderRegisteredCss2dLabels } from './css2d-label-registry';
import { clearCss2dLabelRegistry, renderRegisteredCss2dLabels } from './css2d-label-registry';
import { CullingService } from './culling/culling.service';
import Stats from 'three/examples/jsm/libs/stats.module.js';
import { InspectionPointInstanceManager } from './inspection-point-instance-manager.service';

/**
 * Camera near plane (m) set in init(). animate() raises it for the light model in MODEL
 * navigation (see lightModelCameraNear) and puts it back everywhere else.
 */
export const CAMERA_DEFAULT_NEAR = 0.5;

@Injectable({ providedIn: 'root' })
export class Graphics {
    public hammerManager: HammerManager;
    public axesHammerManager: HammerManager;

    public scene: THREE.Scene = null;
    public customMaterialEnvMap: THREE.Texture = null;
    private axesScene: THREE.Scene = null;
    private scenecss: THREE.Scene = null;
    private scenecss2d: THREE.Scene = null;
    private scenecssAxes: THREE.Scene = null;
    private mesh7: THREE.Mesh;
    private mesh8: THREE.Line;
    private mesh9: THREE.Line;
    private meshNorth: THREE.Mesh;
    private meshSouth: THREE.Mesh;
    private meshEast: THREE.Mesh;
    private meshWest: THREE.Mesh;
    public camera: THREE.Camera = null;
    public axesCamera: THREE.Camera = null;
    private axes: THREE.AxesHelper = null;
    private axesBox: THREE.Mesh = null;
    public axesGroup: THREE.Group = null;
    public axesGroupBox: THREE.Group = null;
    public axesGroupRing: THREE.Group = null;
    public axesGroupVectors: THREE.Group = null;
    public axesGroupTexts: THREE.Group = null;
    public cssGroup2d: THREE.Group = null;

    public renderer: THREE.WebGLRenderer = null;
    private labelRenderer: any = null;
    private axesRenderer: THREE.WebGLRenderer = null;
    private renderercss: CSS3DRenderer = null;
    private axesrenderercss: CSS3DRenderer = null;
    private container;
    private axesContainer: HTMLElement = null;
    private controls: OrbitControls = null;
    private composer: EffectComposer = null;
    public selectionOutlinePass: SelectionOutlinePass = null;
    // was: /** Screen-space ambient occlusion; enabled per frame by shouldApplyAmbientOcclusion in animate(). */
    /** Screen-space ambient occlusion; enabled and blended per frame by decideAmbientOcclusionFrame in animate(). */
    public ambientOcclusionPass: AmbientOcclusionPass = null;
    /** User/QA switch for ambient occlusion; read from localStorage 'c4d.ambientOcclusion' in init(). */
    private _ambientOcclusionEnabled: boolean = true;
    /** QA switch: localStorage 'c4d.ambientOcclusionFade' = 'off' restores the hard on/off switching; read in init(). */
    private _ambientOcclusionFade: boolean = true;
    /**
     * AO world radius (m) per metre of camera distance to the orbit target, light model in MODEL
     * navigation only (see ambientOcclusionWorldRadius). Public so it can be tuned live from the
     * browser console; takes effect on the next computed AO frame (call requestRender()).
     */
    public aoLightModelRadiusPerMetre = AO_LIGHT_MODEL_RADIUS_PER_METRE;
    /**
     * GTAO scale (AO exponent) for the light model in MODEL navigation (see
     * ambientOcclusionWorldRadius). Public so it can be tuned live from the browser console;
     * takes effect on the next computed AO frame (call requestRender()). Non-finite → default.
     */
    public aoLightModelScale = AO_LIGHT_MODEL_SCALE;
    /**
     * World bounding box of asset.lightCampaign.group, for the light model camera near plane.
     * The light model never moves, so it is computed once per group (_lightModelBoxGroup) and
     * kept until a different light model group is shown; null until computed.
     */
    private _lightModelBox: THREE.Box3 | null = null;
    /** The light model group _lightModelBox was computed from. */
    private _lightModelBoxGroup: THREE.Object3D | null = null;
    /** Detects camera motion between rendered frames: AO fades out while the camera moves. */
    private _aoCameraTracker = new CameraMotionTracker();
    /** performance.now() of the last frame the camera moved on; AO waits AO_SETTLE_MS after it. */
    private _aoLastMotionAt: number | null = null;
    /** AO blend intensity applied on the last rendered frame (0..1); the gate ramps it. */
    private _aoIntensity = 0;
    /** performance.now() of the last AO decision; the gate advances its ramps by the elapsed time. */
    private _aoLastFrameAt: number | null = null;
    /** The pass's cached AO texture belongs to the current still camera (gate state; false after motion). */
    private _aoFresh = false;
    public mouseHelper: THREE.Mesh = null;
    public stats: Stats = null;
    /** PERF: When true, the render loop skips OutlinePass, occlusion culling, and shader pre-compilation. */
    public isDragMode: boolean = false;
    /** True while the user is actively drawing a selection shape (set by GraphicsRenderRepair). */
    public isDrawingMode: boolean = false;
    /** When true, the next animate() tick performs the full render pipeline. */
    private _needsRender: boolean = true;
    /** Scratch map reused by precompileOverrideMaterial to avoid per-frame allocations. */
    private _materialSwapCache: Map<THREE.Mesh, THREE.Material | THREE.Material[]> = new Map();
    private _frameCounter: number = 0;
    private static readonly SAFETY_RENDER_INTERVAL = 60;

    public rectd = {
        startRectangleSelection: false,
    };

    private _offsetX: number;
    requestId: number;

    public get Camera(): THREE.Camera {
        return this.camera;
    }
    public get offsetX(): number {
        return this._offsetX;
    }
    public set offsetX(v: number) {
        this._offsetX = v;
    }
    private _offsetY: number;
    public get offsetY(): number {
        return this._offsetY;
    }
    public set offsetY(v: number) {
        this._offsetY = v;
    }

    public get Controls(): OrbitControls {
        return this.controls;
    }

    public get ambientOcclusionEnabled(): boolean {
        return this._ambientOcclusionEnabled;
    }
    public set ambientOcclusionEnabled(v: boolean) {
        this._ambientOcclusionEnabled = v;
        this.requestRender();
    }

    constructor(
      private graphicsLights: GraphicsLights,
      private cullingService: CullingService,
      private instanceManager: InspectionPointInstanceManager,
    ) { }

  destroy(): void {
    console.log('💣 Destroy Graphics');

    // Phase 4 (SEL-04): dispose proxy pool and InstancedMesh state BEFORE
    // ThreeJsCleanup traversals — prevents shared geometry disposal via proxy route.
    this.instanceManager.destroy();

    // Dispose composer and passes
    [this.composer, this.selectionOutlinePass, this.ambientOcclusionPass].forEach(x => {
      ThreeJsCleanup.disposeObject(x);
    });
    this.composer = null;
    this.selectionOutlinePass = null;
    this.ambientOcclusionPass = null;
    this._aoCameraTracker.reset();
    this._aoLastMotionAt = null;
    this._aoIntensity = 0;
    this._aoLastFrameAt = null;
    this._aoFresh = false;
    // Do not keep the previous asset's light model alive through the near-plane box cache.
    this._lightModelBox = null;
    this._lightModelBoxGroup = null;

    // Light-model crease edges: terminate their worker (a late answer is ignored) and dispose
    // their geometries, whether the light model is attached or not.
    this.assetManager?.asset?.lightModelEdges?.dispose();

    // Outside MODEL the hidden light model is detached from asset.group
    // (Asset.syncLightModelAttachment), so the scene walk below would skip its meshes.
    // Dispose it directly rather than re-attach it: this does not depend on asset.group
    // still being in this.scene. An attached light model is left to the scene walk.
    const lightGroup = this.assetManager?.asset?.lightCampaign?.group;
    if (lightGroup && !lightGroup.parent) {
        ThreeJsCleanup.disposeObject(lightGroup);
    }

    // Dispose scenes and objects
    [this.scene, this.axesScene, this.scenecss, this.scenecssAxes, this.scenecss2d].forEach(scene => {
        ThreeJsCleanup.disposeObject(scene);
    });
    // The label registry is module-level: forget labels that were removed with their group
    // but never disposed (e.g. anomaly labels toggled off) so they do not outlive this session.
    clearCss2dLabelRegistry();
    this.scene = null;
    this.axesScene = null;
    this.scenecss = null;
    this.scenecssAxes = null;
    this.scenecss2d = null;

    // Dispose of Three.js objects explicitly
    [this.mesh7, this.mesh8, this.mesh9, this.meshNorth, this.meshSouth, this.meshEast, this.meshWest,
     this.axes, this.axesBox, this.axesCamera, this.axesGroup, this.axesGroupBox, this.axesGroupRing,
     this.axesGroupVectors, this.axesGroupTexts, this.camera, this.container, this.axesContainer, this.cssGroup2d,
     this.mouseHelper].forEach(object => {
        ThreeJsCleanup.disposeObject(object);
    });
    this.container = null;
    this.axesContainer = null;
    this.camera = null;
    this.axesCamera = null;
    this.axesGroup = null;
    this.axesGroupBox = null;
    this.axesGroupRing = null;
    this.axesGroupVectors = null;
    this.axesGroupTexts = null;
    this.mouseHelper = null;

    // Dispose renderers
    [this.renderer, this.axesRenderer].forEach(renderer => {
        ThreeJsCleanup.disposeRenderer(renderer);
    });
    this.renderer = null;
    this.axesRenderer = null;

    // Dispose CSS renderers (just nullify)
    this.renderercss = null;
    this.axesrenderercss = null;
    this.labelRenderer = null;

    // Dispose controls
    ThreeJsCleanup.disposeControls(this.controls);
    this.controls = null;

    if (this.stats) {
        if (this.stats.dom && this.stats.dom.parentNode) {
            this.stats.dom.parentNode.removeChild(this.stats.dom);
        }
        this.stats = null;
    }

    // Cancel animation frames
    ThreeJsCleanup.cancelAnimation(this.requestId);
    this.requestId = undefined;

    // Remove event listeners if any exist
    window.removeEventListener('resize', this.onWindowResize);

    // Destroy hammer managers
    this.hammerManager.destroy();
    this.axesHammerManager.destroy();
    this.hammerManager = null;
    this.axesHammerManager = null;

    // Clear culling service
    this.cullingService.clear();
  }

  public spriteInfos: Label2DInfo[] = [];//createLabelSprites(scene, labels);

  public createReferenceLines(referenceLines: Array<any>, centerPoint: THREE.Vector3): void {
    // 2) Build labels[], one entry per incoming line, but only the
    //    first run per (label + direction) keeps the text—others become ''.
    const LABEL_DIR_THRESHOLD = 0.9;  // cos(angle) > 0.9 => same direction

    // track which (label, direction‐group) have already been used
    const usedGroups: Record<string, THREE.Vector3[]> = {};

    const labels: LabelData[] = referenceLines.map(item => {
    // NewGeometryL is an edited override of the line; the conversor exports it as an empty array
    // (not null) when there is no override, so `??` alone would hide every line. Fall back to
    // GeometryL whenever NewGeometryL has no points.
    const geometry = item.Geometries3D?.[0];
    const coords: number[] = geometry?.NewGeometryL?.length ? geometry.NewGeometryL : (geometry?.GeometryL ?? []);
    // turn flat coords → Vector3 list
    const p0 = new THREE.Vector3(coords[0], coords[1], coords[2]);
    const pN = new THREE.Vector3(
        coords[coords.length - 3],
        coords[coords.length - 2],
        coords[coords.length - 1]
    );
    const dir = pN.clone().sub(p0).normalize();

    let labelText = '';
    if (item.Label) {
        const lbl = item.Label as string;
        const groups = (usedGroups[lbl] ||= []);

        // see if dir matches any used group
        const match = groups.find(g => Math.abs(g.dot(dir)) > LABEL_DIR_THRESHOLD);
        if (!match) {
        // first time we see this direction for this label
        groups.push(dir);
        labelText = lbl;
        }
    }

    return {
        line: coords,
        label: labelText
    };
    });


    const groupedLines = labels.reduce((acc, item) => {
        const label = item.label || 'Unlabeled';
        if (!acc[label]) {
          acc[label] = [];
        }
        acc[label].push(item);
        return acc;
      }, {});

      for(const label in groupedLines){
        const lines = groupedLines[label];

        const line = lines.sort((a, b) => new THREE.Vector3(a.line[0], a.line[1], a.line[2]).distanceTo(new THREE.Vector3(a.line[a.line.length - 3], a.line[a.line.length - 2], a.line[a.line.length - 1])) > 
        new THREE.Vector3(b.line[0], b.line[1], b.line[2]).distanceTo(new THREE.Vector3(b.line[b.line.length - 3], b.line[b.line.length - 2], b.line[b.line.length - 1])) && 1 || -1)

        for (let index = 0; index < line.length -1 ; index++) {
            line[index].label = "";
        }
      }

    // 3) Now render all lines + labels in one go:
    createLabel2DObjects(
    this.cssGroup2d as any,
    labels,
    centerPoint as any,
    this.camera as any,
    this.labelRenderer
    );
    this.requestRender();
    }

    public disposeReferenceLines(): void {
        disposeGroupLinesAndLabels(this.cssGroup2d as any);
        this.requestRender();
    }

    private sortPoints(line: Array<THREE.Vector3>): Array<THREE.Vector3> {
        const lineBox = new THREE.Box3();
        line.forEach(l => {
            lineBox.expandByPoint(l);
        });

        const size = new THREE.Vector3();
        lineBox.getSize(size);

        if (size.x > size.y && size.x > size.z) {
            line.sort((a, b) => a.x > b.x && 1 || -1)
        }
        else if (size.y > size.x && size.y > size.z) {
            line.sort((a, b) => a.y > b.y && 1 || -1)
        }
        else {
            line.sort((a, b) => a.z > b.z && 1 || -1)
        }

        let current = line[0];
        line.splice(0, 1);
        const newLine = [current];
        while (line.length > 1) {
            current = line.reduce((a, b) => current.distanceTo(a) < current.distanceTo(b) ? a : b);
            let index = line.findIndex(x => x.equals(current));
            if (index > -1) {
                line.splice(index, 1);
                newLine.push(current);
            }
        }
        //
        newLine.push(line[0]);


        return newLine;
    }

    private uniqueValues(array: Array<THREE.Vector3>): Array<THREE.Vector3> {
        const uniqueValuesArray = new Array<THREE.Vector3>();

        for (const value of array) {
            const index = uniqueValuesArray.findIndex(x => x.x === value.x && x.y === value.y && x.z === value.z)
            if (index === -1) {
                uniqueValuesArray.push(value);
            }
        }

        return uniqueValuesArray;
    }
    private oldAxesRotation: THREE.Euler;
    public animate() {
        if (this) {;
            this.requestAnimation();
            if (this.stats) {
                this.stats.update();
            }

            // Auto-detect dirty instance buffers
            if (this.instanceManager.hasDirtyBuffers()) {
                this._needsRender = true;
            }

            // Whether this frame was asked for (requestRender, dirty instance buffers, drag)
            // rather than only forced by the safety counter below.
            const renderRequested = this._needsRender || this.isDragMode;

            // Safety fallback: force render every ~1 second
            this._frameCounter++;
            if (this._frameCounter >= Graphics.SAFETY_RENDER_INTERVAL) {
                this._frameCounter = 0;
                this._needsRender = true;
            }

            // RENDER GATE: skip all expensive work when scene is idle
            if (!this._needsRender && !this.isDragMode) return;
            this._needsRender = false;

            this.axesCamera.position.copy(this.camera.position);
            this.axesCamera.position.sub(this.controls.target);
            this.axesCamera.position.setLength(500);
            this.axesCamera.lookAt(this.axesScene.position);

            if (this.meshNorth && this.oldAxesRotation) {
                var vector = new THREE.Vector3(0, 0, - 1);
                vector.applyQuaternion(this.axesCamera.quaternion);
                this.axesCamera.getWorldDirection(vector);

                let yRotation = this.oldAxesRotation.y - this.axesCamera.rotation.y;
                let xRotation = this.oldAxesRotation.x - this.axesCamera.rotation.x;


                this.meshNorth.lookAt(this.axesCamera.position)
                this.meshSouth.lookAt(this.axesCamera.position)
                this.meshEast.lookAt(this.axesCamera.position)
                this.meshWest.lookAt(this.axesCamera.position)


                this.meshNorth.matrixWorldNeedsUpdate = true;
                this.meshSouth.matrixWorldNeedsUpdate = true;
                this.meshEast.matrixWorldNeedsUpdate = true;
                this.meshWest.matrixWorldNeedsUpdate = true;


                this.axesRenderer.render(this.axesScene, this.axesCamera);

                this.oldAxesRotation = this.axesCamera.rotation.clone();
                // console.log('rotation', this.mesh7)
            }
            else {
                this.oldAxesRotation = this.axesCamera.rotation.clone();
            }

            // PERF: take the hidden light model out of the scene graph (and put it back once a
            // light region is shown) so updateMatrixWorld and the CSS2D pass do not walk ~16k
            // hidden Object3Ds every frame. Runs before this frame's render: no requestRender.
            const assetForLightModel = this.assetManager?.asset;
            assetForLightModel?.syncLightModelAttachment?.();

            // The light model in MODEL navigation is seen from hundreds of metres, where near 0.5
            // leaves the 24-bit depth too coarse and GTAO speckles: push the near plane out to half
            // the distance to the light model's box (lightModelCameraNear). Every other view gets
            // CAMERA_DEFAULT_NEAR back on its first frame. Runs on every rendered frame, before the
            // culler and the AO camera tracker read the projection and before this frame's render;
            // a pure function of the pose, so a still camera never reads as moving.
            this.syncCameraNear(assetForLightModel);

            // PERF: Skip culling during drag — camera is static, results don't change.
            // On idle safety-only renders, cull only if the camera, mesh visibility or
            // occluder materials changed since the last cull (some app paths change
            // materials/visibility without calling requestRender).
            if (!this.isDragMode) {
              if (renderRequested) {
                this.cullingService.cull(this.camera);
              } else {
                this.cullingService.cullIfChanged(this.camera);
              }
            }

            // Flush dirty InstancedMesh matrix/color buffers to GPU before rendering.
            this.instanceManager.flushDirtyBuffers();

            // PERF: Skip shader pre-compilation during drag — no new batches are created
            // renderer.compile() reads object.material directly and ignores scene.overrideMaterial,
            // so the outline mask material must be temporarily swapped onto each mesh
            // for the precompile to actually warm up its shader program (otherwise async
            // KHR_parallel_shader_compile drops the first outline frames after a batch grows).
            if (!this.isDragMode && this.selectionOutlinePass && this.instanceManager.needsCompile) {
              this.renderer.compile(this.scene, this.camera);
              this.precompileOverrideMaterial(this.selectionOutlinePass.getMaskMaterialForWarmup());
              this.instanceManager.clearCompileFlag();
            }

            // PERF: Disable the outline pass when not needed (drag mode OR no selection)
            if (this.selectionOutlinePass) {
              this.selectionOutlinePass.enabled = !this.isDragMode && this.selectionOutlinePass.hasSelection;
            }

            // Ambient occlusion fades out over the first frames of camera motion (re-blending
            // the last still camera's AO: computing it costs up to ~90 ms per frame in
            // cluttered interior views) and back in once the camera has been still for
            // AO_SETTLE_MS, outside the see-through and FEA view modes (see shouldApplyAmbientOcclusion). controls.update() runs after the render below
            // and lookAt() does not refresh matrixWorld, so update it before comparing poses.
            if (this.ambientOcclusionPass) {
              this.camera.updateMatrixWorld();
              const asset = this.assetManager?.asset;
              const aoFrame = decideAmbientOcclusionFrame({
                gate: {
                  enabled: this._ambientOcclusionEnabled,
                  // was: viewMode: asset?.viewMode, (default view mode only)
                  viewMode: asset?.viewMode,
                  structureTransparent: GraphicsMaterials.basicMaterial.opacity < 1 || !GraphicsMaterials.basicMaterial.depthWrite,
                  navigationMode: asset?.mode,
                  isLightModel: !!asset?.isLightModel,
                  isDragMode: this.isDragMode,
                },
                moved: this._aoCameraTracker.update(this.camera),
                now: performance.now(),
                lastMotionAt: this._aoLastMotionAt,
                intensity: this._aoIntensity,
                lastFrameAt: this._aoLastFrameAt,
                fade: this._ambientOcclusionFade,
                aoFresh: this._aoFresh,
              });
              this._aoLastMotionAt = aoFrame.lastMotionAt;
              this._aoIntensity = aoFrame.intensity;
              this._aoLastFrameAt = aoFrame.lastFrameAt;
              this._aoFresh = aoFrame.aoFresh;
              this.ambientOcclusionPass.enabled = aoFrame.applyAo;
              this.ambientOcclusionPass.blendIntensity = aoFrame.intensity;
              // Ramp frames re-blend the cached AO texture; only a settled still camera computes.
              this.ambientOcclusionPass.reuseAo = !aoFrame.computeAo;
              // The light model in MODEL navigation is seen from hundreds of metres, where the
              // fixed 1 m radius is ~1 px: scale it with the distance to the orbit target. Every
              // other view gets AO_DEFAULT_PARAMETERS back. Uniforms only, set when computing.
              if (aoFrame.computeAo) {
                const aoRadius = ambientOcclusionWorldRadius({
                  navigationMode: asset?.mode,
                  isLightModel: !!asset?.isLightModel,
                  cameraDistance: this.controls ? this.camera.position.distanceTo(this.controls.target) : this.camera.position.length(),
                  radiusPerMetre: this.aoLightModelRadiusPerMetre,
                  lightModelScale: this.aoLightModelScale,
                });
                // was: this.ambientOcclusionPass.setWorldRadius(aoRadius.radius, aoRadius.thickness);
                this.ambientOcclusionPass.setWorldParameters(aoRadius);
              }
              // Keep rendering through the settle window and the ramps so AO fades in once
              // the window ends and the fade-out reaches 0. Never requested with AO off or
              // in a view that excludes it: the render gate then behaves exactly as before.
              if (aoFrame.requestRender) {
                this.requestRender();
              }
            }

            this.renderAnimation();
            this.preCalc();
            this.renderercss.render(this.scenecss, this.camera);
            this.axesrenderercss.render(this.scenecssAxes, this.axesCamera);
            // CSS2DRenderer.render updated and walked all ~8k-54k scene objects each frame; only the registered labels are positioned now.
            // was: this.labelRenderer.render(this.scene, this.camera);
            renderRegisteredCss2dLabels(this.labelRenderer, this.scene, this.camera);
            this.postCalc();
            this.controls.update();

            // Update label scales based on camera distance
            this.triggerLabelScaleUpdate();

            updateLabel2DPositions(this.spriteInfos, this.camera as any);
        }
    }

    /**
     * Sets camera.near from lightModelCameraNear for this frame and refreshes the projection,
     * only when the value changed. The light model's box is computed once per light model group,
     * only while it is attached in MODEL navigation (outside MODEL Asset.syncLightModelAttachment
     * detaches it, and detached meshes keep their last matrixWorld).
     */
    private syncCameraNear(asset: any): void {
        const camera = this.camera as THREE.PerspectiveCamera;
        if (!camera?.isPerspectiveCamera) return;
        const isLightModelInModel = asset?.mode === 'MODEL' && !!asset?.isLightModel;
        const lightGroup: THREE.Object3D | undefined = asset?.lightCampaign?.group;
        if (isLightModelInModel && lightGroup?.parent && lightGroup !== this._lightModelBoxGroup) {
            lightGroup.updateWorldMatrix(true, true);
            const box = new THREE.Box3().setFromObject(lightGroup);
            // Not cached while empty: the light model GLTF may still be loading into the group.
            if (!box.isEmpty()) {
                this._lightModelBox = box;
                this._lightModelBoxGroup = lightGroup;
            }
        }
        const near = lightModelCameraNear({
            navigationMode: asset?.mode,
            isLightModel: !!asset?.isLightModel,
            cameraPosition: camera.position,
            lightModelBox: lightGroup && lightGroup === this._lightModelBoxGroup ? this._lightModelBox : null,
            orbitTarget: this.controls?.target ?? null,
            defaultNear: CAMERA_DEFAULT_NEAR,
        });
        if (camera.near !== near) {
            camera.near = near;
            camera.updateProjectionMatrix();
        }
    }

    public renderAnimation() {
        this.composer.render();
        this.axesRenderer.render(this.axesScene, this.axesCamera);
    }

    /**
     * Updates the scale of labels based on camera distance
     */
    private updateLabelScales(): void {
        // This method will be called by AssetManager
        // The actual implementation is in AssetManager.updateLabelScales()
    }

    /**
     * Public method to trigger label scale updates
     */
    public triggerLabelScaleUpdate(): void {
        if (this.assetManager) {
            this.assetManager.triggerLabelScaleUpdate();
        }
    }

    /**
     * Reference to AssetManager for label scale updates
     */
    private assetManager: any = null;

    /**
     * Sets the AssetManager reference
     */
    public setAssetManager(assetManager: any): void {
        this.assetManager = assetManager;
    }

    public toggleStats(): void {
        if (this.stats) {
            const display = this.stats.dom.style.display;
            this.stats.dom.style.display = (display === 'none') ? 'block' : 'none';
        }
    }

    public stopAnimate() {
        if (this.requestId) {
            cancelAnimationFrame(this.requestId);
            this.requestId = undefined;
        }
    }

    public rebuildCulling(): void {
        if (this.scene) {
            this.cullingService.rebuild();
            this.requestRender();
        }
    }

    public requestAnimation() {
        // animate() re-schedules itself on every frame, so scheduling without
        // cancelling the pending request would start an ADDITIONAL self-
        // perpetuating render loop on every call (each one a full render plus,
        // with zone-patched rAF, a full change-detection tick per frame).
        // Cancelling first keeps exactly one loop alive no matter how often
        // features call this.
        if (this.requestId) {
            cancelAnimationFrame(this.requestId);
        }
        this.requestId = requestAnimationFrame(this.animate.bind(this));
    }

    /**
     * Pre-compile an override material against every Mesh in the scene by temporarily
     * assigning it as the mesh's material. `renderer.compile()` reads `object.material`
     * (it never consults `scene.overrideMaterial`), so this swap is the only way to
     * warm up shader programs for OutlinePass-style override materials ahead of the
     * first render that actually uses them.
     */
    private precompileOverrideMaterial(overrideMaterial: THREE.Material): void {
      if (!overrideMaterial) return;
      this._materialSwapCache.clear();
      this.scene.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (!mesh.isMesh) return;
        this._materialSwapCache.set(mesh, mesh.material);
        mesh.material = overrideMaterial;
      });
      this.renderer.compile(this.scene, this.camera);
      for (const [mesh, mat] of this._materialSwapCache) {
        mesh.material = mat;
      }
      this._materialSwapCache.clear();
    }

    /** Signal that the scene changed and needs re-rendering. */
    public requestRender(): void {
        this._needsRender = true;
    }

    public preCalc = () => { }; // used by GraphicsRenderDocuments - initial value assigned there
    public postCalc = () => { }; // used by GraphicsRenderDocuments - initial value assigned there

    public init(x, y, z, addlights: boolean) {
        this.resetToDefault();
        this.scene = null;
        this.offsetX = 0;
        this.offsetY = 50;
        if (Util.isNullOrUndefined(this.scene)) {
            this.scene = new THREE.Scene();
        }

        this.scenecss = new THREE.Scene();
        this.scenecssAxes = new THREE.Scene();
        this.scenecss2d = new THREE.Scene();
        const SCREEN_WIDTH = window.innerWidth;
        const SCREEN_HEIGHT = window.innerHeight;
        const VIEW_ANGLE = 60,
            ASPECT = SCREEN_WIDTH / SCREEN_HEIGHT,
            // was: NEAR = 0.5,
            NEAR = CAMERA_DEFAULT_NEAR,
            FAR = 5000;
        this.camera = new THREE.PerspectiveCamera(VIEW_ANGLE, ASPECT, NEAR, FAR);
        this.graphicsLights.setSpotlightFromCamera(this.camera);

        this.camera.up.set(x, y, z);
        this.scene.add(this.camera);
        this.camera.lookAt(this.scene.position);

        const canvas = document.createElementNS(
            'http://www.w3.org/1999/xhtml',
            'canvas',
        ) as HTMLCanvasElement;
        const contextAttributes = {
            antialias: false,
            powerPreference: 'high-performance',
        };
        let context;
        if (WebGL.isWebGL2Available()) {
            console.log('Using WebGL2');
            context = canvas.getContext('webgl2', contextAttributes);
        } else {
            console.log('Using WebGL');
            context =
                canvas.getContext('webgl', contextAttributes) ||
                canvas.getContext('experimental-webgl', contextAttributes);
        }

        this.renderer = new THREE.WebGLRenderer({
            canvas,
            context,
        });
        this.renderer.domElement.dataset.viewportLayer = 'webgl';

        this.renderer.setRenderTarget(null);
        this.renderercss = new CSS3DRenderer();
        this.renderercss.domElement.dataset.viewportLayer = 'css3d';
        
        this.axesrenderercss = new CSS3DRenderer();
        this.renderercss.setSize(window.innerWidth, window.innerHeight);
        this.axesrenderercss.setSize(100, 100);
        this.renderercss.domElement.style.position = 'absolute';
        this.axesrenderercss.domElement.style.position = 'absolute';
        this.renderercss.domElement.style.top = '0';
        this.axesrenderercss.domElement.style.top = '0';
        this.renderer.setPixelRatio(2);
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.updateLineMaterialResolutions();

        this.renderer.sortObjects = true;
        this.renderer.setClearColor(0xffffff, 0);

        this.container = document.getElementById('ThreeJS');
        this.container.appendChild(this.renderer.domElement);
        this.container.appendChild(this.renderercss.domElement);

        this.renderer.setRenderTarget(null);
        // three.js update: RoomEnvironment is now a Scene subclass. Reproduce the
        // old vendored helper (which returned a PMREM env-map texture) via PMREMGenerator.
        const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
        this.customMaterialEnvMap = pmremGenerator.fromScene(new RoomEnvironment()).texture;
        pmremGenerator.dispose();
        this.labelRenderer = initCSS2DRenderer(this.container);
        this.labelRenderer.setSize(window.innerWidth, window.innerHeight);

        this.stats = new Stats();
        this.stats.dom.style.display = 'none';
        this.container.appendChild(this.stats.dom);


        // window.addEventListener('resize', () => {
        //     const w = this.container.clientWidth;
        //     const h = this.container.clientHeight;
        //     this.renderer.setSize( w, h );
        //     this.camera.aspect = w / h;
        //     this.camera.updateProjectionMatrix();
        //     this.labelRenderer.setSize( w, h );
        //   });
        window.addEventListener('resize', this.onWindowResize.bind(this), false);
        this.onWindowResize();

        this.controls = new OrbitControls(this.camera, this.renderercss.domElement);
        this.controls.screenSpacePanning = true;
        this.controls.addEventListener('change', () => this.requestRender());

        /// Axis Data
        this.axesContainer = document.getElementById('axisHelper');
        this.axesRenderer = new THREE.WebGLRenderer({ alpha: true });

        this.axesRenderer.setSize(100, 100);
        this.axesContainer.appendChild(this.axesRenderer.domElement);
        this.axesContainer.appendChild(this.axesrenderercss.domElement);
        // scene
        this.axesScene = new THREE.Scene();
        this.axesCamera = new THREE.PerspectiveCamera(60, 1, 0.001, 1000);
        this.axesCamera.up = this.camera.up; // important!

        // axes
        this.axes = new THREE.AxesHelper(100);
        this.axesScene.add(this.axes);
        const boxGeometry = new THREE.BoxGeometry();
        this.axesBox = new THREE.Mesh(boxGeometry, GraphicsMaterials.colorToBasicMaterial(0x00ff00));
        this.axesBox.geometry.scale(200, 200, 200);
        const boxHelper = new THREE.BoxHelper(this.axesBox, 0xffffff);
        boxHelper.visible = true;
        this.axesScene.add(boxHelper);
        this.axesBox.visible = true;
        this.axesBox.name = "intersectionBox";

        // postprocessing
        // this.composer = new EffectComposer(this.renderer); // replaced: the AO pass needs scene depth in both composer buffers
        // Same HalfFloat target the composer builds by default, plus a DepthTexture so
        // RenderPass's depth can be sampled by the AO pass. The composer uses this target
        // and a clone of it; RenderTarget.copy clones the DepthTexture (own Source), so
        // both alternating buffers carry their own depth, and WebGLTextures resizes each
        // depth texture with its target on composer.setSize.
        const composerSize = this.renderer.getSize(new THREE.Vector2());
        const composerPixelRatio = this.renderer.getPixelRatio();
        const composerWidth = composerSize.width * composerPixelRatio;
        const composerHeight = composerSize.height * composerPixelRatio;
        const composerTarget = new THREE.WebGLRenderTarget(composerWidth, composerHeight, {
            type: THREE.HalfFloatType,
            depthTexture: new THREE.DepthTexture(composerWidth, composerHeight),
        });
        composerTarget.texture.name = 'EffectComposer.rt1';
        this.composer = new EffectComposer(this.renderer, composerTarget);
        // With an explicit target the composer takes the (already pixel-ratio scaled)
        // target size as its logical size, so passes added next would be sized at
        // width * pr * pr. Reset the logical size before adding any pass.
        this.composer.setSize(composerSize.width, composerSize.height);
        const renderPass = new RenderPass(this.scene, this.camera);
        this.composer.addPass(renderPass);

        // AO right after the scene render, before the selection outline and the
        // linear → sRGB OutputPass. composer.addPass/setSize hand it the pixel-ratio
        // scaled size; the pass halves it internally. Re-running init() replaces the
        // pass, so free the previous one and restart motion tracking for the new camera.
        try {
            this._ambientOcclusionEnabled = localStorage.getItem('c4d.ambientOcclusion') !== 'off';
            this._ambientOcclusionFade = localStorage.getItem('c4d.ambientOcclusionFade') !== 'off';
        } catch {
            // Storage disabled (SecurityError): keep the defaults, AO on and fading.
            this._ambientOcclusionEnabled = true;
            this._ambientOcclusionFade = true;
        }
        this.ambientOcclusionPass?.dispose();
        this._aoCameraTracker.reset();
        this._aoLastMotionAt = null;
        this._aoIntensity = 0;
        this._aoLastFrameAt = null;
        this._aoFresh = false;
        this.ambientOcclusionPass = new AmbientOcclusionPass(this.scene, this.camera, composerWidth, composerHeight);
        this.composer.addPass(this.ambientOcclusionPass);
        // Compile the AO full-screen shaders now rather than on the first AO frame.
        this.ambientOcclusionPass.warmUpShaders(this.renderer, this.composer.renderTarget1);

        this.mouseHelper = new THREE.Mesh(
            new THREE.BoxGeometry(1, 1, 10),
            new THREE.MeshNormalMaterial(),
        );

        // Single custom selection outline pass — replaces the two stock OutlinePass
        // instances at a fraction of the cost (see SelectionOutlinePass docs).
        this.selectionOutlinePass = new SelectionOutlinePass(
            new THREE.Vector2(window.innerWidth, window.innerHeight),
            this.scene,
            this.camera,
        );
        this.selectionOutlinePass.primaryColor.set(0xccffff);
        this.selectionOutlinePass.secondaryColor.set(0x0247b5);
        this.selectionOutlinePass.edgeThickness = 3;
        this.composer.addPass(this.selectionOutlinePass);
        // The outline mask render draws the selected meshes through geometry, but three.js
        // renders nothing under a culled section proxy: pin sections with outlined meshes.
        this.cullingService.setConfig({
            pinnedLayerMask: (1 << OUTLINE_LAYER_PRIMARY) | (1 << OUTLINE_LAYER_SECONDARY)
        });

        // OutputPass LAST — converts linear → sRGB
        const outputPass = new OutputPass();
        this.composer.addPass(outputPass);
        this.cssGroup2d = new THREE.Group();

        this.mouseHelper.visible = false;
        this.scene.add(this.mouseHelper);
        this.scene.add(this.cssGroup2d);

        this.graphicsLights.addLights(this.scene, this.camera, this.controls);

        this.createAxesBox();
    }

    private createAxesBox() {

        /////////////// x
        const face1 = new THREE.BufferGeometry();
        face1.setAttribute('position', new THREE.BufferAttribute(new Float32Array(
            [100, 100, 100,
                100, -100, 100,
                100, -100, -100,
                100, 100, 100,
                100, -100, -100,
                100, 100, -100]
        ), 3));
        const face2 = new THREE.BufferGeometry();
        face2.setAttribute('position', new THREE.BufferAttribute(new Float32Array(
            [-100, 100, 100,
            -100, -100, 100,
            -100, -100, -100,
            -100, 100, 100,
            -100, -100, -100,
            -100, 100, -100]), 3));
        /////////////// y
        const face3 = new THREE.BufferGeometry();
        face3.setAttribute('position', new THREE.BufferAttribute(new Float32Array(
            [100, 100, 100,
                -100, 100, 100,
                -100, 100, -100,
                100, 100, 100,
                -100, 100, -100,
                100, 100, -100]), 3));
        const face4 = new THREE.BufferGeometry();
        face4.setAttribute('position', new THREE.BufferAttribute(new Float32Array(
            [100, -100, 100,
                -100, -100, 100,
                -100, -100, -100,
                100, -100, 100,
                -100, -100, -100,
                100, -100, -100]), 3));
        /////////////// z
        const face5 = new THREE.BufferGeometry();
        face5.setAttribute('position', new THREE.BufferAttribute(new Float32Array(
            [100, 100, 100,
                -100, 100, 100,
                -100, -100, 100,
                100, 100, 100,
                -100, -100, 100,
                100, -100, 100]), 3));
        const face6 = new THREE.BufferGeometry();
        face6.setAttribute('position', new THREE.BufferAttribute(new Float32Array(
            [100, 100, -100,
                -100, 100, -100,
                -100, -100, -100,
                100, 100, -100,
                -100, -100, -100,
                100, -100, -100]), 3));



        // Orientation Sides

        this.axesGroupTexts = new THREE.Group();
        var textLabelNorth: TextGeometry
        var textLabelSouth: TextGeometry
        var textLabelEast: TextGeometry
        var textLabelWest: TextGeometry


        const loader = new FontLoader();
        loader.load(
            'statics/fonts/nucleo/helvetiker_bold.typeface.json', (myFont) => {
                textLabelNorth = new TextGeometry('FWD', {
                    font: myFont,
                    size: 45,
                    depth: 2,
                });
                textLabelSouth = new TextGeometry('AFT', {
                    font: myFont,
                    size: 45,
                    depth: 2,
                });
                textLabelEast = new TextGeometry('SB', {
                    font: myFont,
                    size: 45,
                    depth: 2,
                });
                textLabelWest = new TextGeometry('PS', {
                    font: myFont,
                    size: 45,
                    depth: 2,
                });


                this.meshNorth = new THREE.Mesh(textLabelNorth, GraphicsMaterials.axisBoxLabelMaterial);
                this.meshSouth = new THREE.Mesh(textLabelSouth, GraphicsMaterials.axisBoxLabelMaterial);
                this.meshEast = new THREE.Mesh(textLabelEast, GraphicsMaterials.axisBoxLabelMaterial);
                this.meshWest = new THREE.Mesh(textLabelWest, GraphicsMaterials.axisBoxLabelMaterial);


                this.meshNorth.geometry.center()
                this.meshSouth.geometry.center()
                this.meshEast.geometry.center()
                this.meshWest.geometry.center()


                this.meshNorth.name = "North"
                this.meshSouth.name = "South"
                this.meshEast.name = "East"
                this.meshWest.name = "West"

                this.meshNorth.visible = true
                this.meshSouth.visible = true
                this.meshEast.visible = true
                this.meshWest.visible = true

                this.meshNorth.userData.scale = 1;
                this.meshSouth.userData.scale = 1;
                this.meshEast.userData.scale = 1;
                this.meshWest.userData.scale = 1;

                this.meshNorth.up.set(0, 0, 1);
                this.meshSouth.up.set(0, 0, 1);
                this.meshEast.up.set(0, 0, 1);
                this.meshWest.up.set(0, 0, 1);

                this.meshNorth.rotation.set(Math.PI / 2, Math.PI / 2, 0);
                this.meshSouth.rotation.set(Math.PI / 2, -Math.PI / 2, 0);
                this.meshEast.rotation.set(Math.PI / 2, 0, 0);
                this.meshWest.rotation.set(Math.PI / 2, Math.PI, 0);

                this.meshNorth.position.set(200, 0, -100);
                this.meshSouth.position.set(-200, 0, -100);
                this.meshEast.position.set(0, -200, -100);
                this.meshWest.position.set(0, 200, -100);


                this.axesGroupTexts.add(this.meshNorth)
                this.axesGroupTexts.add(this.meshSouth)
                this.axesGroupTexts.add(this.meshEast)
                this.axesGroupTexts.add(this.meshWest)

            });

        // vectors underground

        const vectors1 = [];
        const vectors2 = [];
        vectors1.push(new THREE.Vector3(-200, 0, 0));
        vectors1.push(new THREE.Vector3(200, 0, 0));
        vectors2.push(new THREE.Vector3(0, -200, 0));
        vectors2.push(new THREE.Vector3(0, 200, 0));
        const crossVectors = new THREE.BufferGeometry().setFromPoints(vectors1);
        const crossVectors2 = new THREE.BufferGeometry().setFromPoints(vectors2);


        const orientation = new THREE.RingBufferGeometry(170, 200, 32);


        this.axesGroup = new THREE.Group();
        this.axesGroupBox = new THREE.Group();
        this.axesGroupRing = new THREE.Group();
        this.axesGroupVectors = new THREE.Group();


        var mesh1 = new THREE.Mesh(face1, GraphicsMaterials.axisBoxMaterial);
        var mesh2 = new THREE.Mesh(face2, GraphicsMaterials.axisBoxMaterial);
        var mesh3 = new THREE.Mesh(face3, GraphicsMaterials.axisBoxMaterial);
        var mesh4 = new THREE.Mesh(face4, GraphicsMaterials.axisBoxMaterial);
        var mesh5 = new THREE.Mesh(face5, GraphicsMaterials.axisBoxMaterial);
        var mesh6 = new THREE.Mesh(face6, GraphicsMaterials.axisBoxMaterial);
        this.mesh7 = new THREE.Mesh(orientation, GraphicsMaterials.axisBoxMaterial);
        this.mesh8 = new THREE.Line(crossVectors, GraphicsMaterials.axisBoxVectorMaterial);
        this.mesh9 = new THREE.Line(crossVectors2, GraphicsMaterials.axisBoxVectorMaterial);


        mesh1.name = "Face X Positive"
        mesh2.name = "Face X Negative"
        mesh3.name = "Face Y Positive"
        mesh4.name = "Face Y Negative"
        mesh5.name = "Face Z Positive"
        mesh6.name = "Face Z Negative"
        this.mesh7.name = "Orientation"
        this.mesh8.name = "Vectors"
        this.mesh9.name = "Vectors2"


        mesh1.visible = true
        mesh2.visible = true
        mesh3.visible = true
        mesh4.visible = true
        mesh5.visible = true
        mesh6.visible = true
        this.mesh7.visible = true
        this.mesh8.visible = true
        this.mesh9.visible = true


        mesh1.userData.scale = 1;
        mesh2.userData.scale = 1;
        mesh3.userData.scale = 1;
        mesh4.userData.scale = 1;
        mesh5.userData.scale = 1;
        mesh6.userData.scale = 1;
        this.mesh7.userData.scale = 1;
        this.mesh8.userData.scale = 1;
        this.mesh9.userData.scale = 1;


        this.mesh7.position.set(0, 0, -100);
        this.mesh8.position.set(0, 0, -100);
        this.mesh9.position.set(0, 0, -100);


        this.axesGroupBox.add(mesh1);
        this.axesGroupBox.add(mesh2);
        this.axesGroupBox.add(mesh3);
        this.axesGroupBox.add(mesh4);
        this.axesGroupBox.add(mesh5);
        this.axesGroupBox.add(mesh6);
        this.axesGroupRing.add(this.mesh7);
        this.axesGroupVectors.add(this.mesh8);
        this.axesGroupVectors.add(this.mesh9);


        this.axesGroup.add(this.axesGroupBox)
        this.axesGroup.add(this.axesGroupRing)
        this.axesGroup.add(this.axesGroupVectors)
        this.axesGroup.add(this.axesGroupTexts)

        this.axesScene.add(this.axesGroup);
    }

    public onWindowResize(): void {
        const marginGroupIconCenterX = 7;
        const marginGroupIconCenterY = 33;
        const distanceFromCamera = 70;

        const marginX = marginGroupIconCenterX * 4;
        const marginY = marginGroupIconCenterY * 4;
        const ndcX = ((window.innerWidth - marginX) / window.innerWidth) * 2 - 1;
        const ndcY = ((window.innerHeight - marginY) / window.innerHeight) * 2 - 1;

        const rayc = new THREE.Raycaster();
        rayc.setFromCamera(new THREE.Vector2(ndcX, ndcY), this.camera);
        const newpos = new THREE.Vector3();
        rayc.ray.at(distanceFromCamera, newpos);
        this.camera.worldToLocal(newpos);

        /*
         * resize to window size
         */
        const width = window.innerWidth;
        const height = window.innerHeight;
        this.renderercss.setSize(width, height);
        this.renderer.setSize(width, height);
        // Resize the post-processing composer (and the attached scene depth texture +
        // outline mask RT). Previously omitted — the composer/outline never resized,
        // so the outline drifted out of alignment after a window resize.
        this.composer?.setSize(width, height);
        (this.camera as PerspectiveCamera).aspect = width / height;
        (this.camera as PerspectiveCamera).updateProjectionMatrix();
        this.updateLineMaterialResolutions();
        this.requestRender();
    }

    private updateLineMaterialResolutions(): void {
        const w = window.innerWidth;
        const h = window.innerHeight;
        GraphicsMaterials.stiffenerMaterial.resolution.set(w, h);
        GraphicsMaterials.selectedLineMaterial.resolution.set(w, h);
        GraphicsMaterials.stiffenerMaterialDashed.resolution.set(w, h);
        GraphicsMaterials.flangeMaterial.resolution.set(w, h);
        GraphicsMaterials.bracketMaterial.resolution.set(w, h);
        GraphicsMaterials.referenceLineMaterial.resolution.set(w, h);
        GraphicsMaterials.borderMaterial.resolution.set(w, h);
    }

    public resetToDefault(): void {
        // this.selectionFlag = -1;
        // this.mode = 'Model';
        // this._sectionNavigator = [];
        // this._regionNavigator = [];
        // this._propertiesNavigator = {};
        // this._filteredSections = [];
        // this.shapesList = [];
        // this.assetNavigator = null;
        // this.homePosition = null;
        // this.bbox = null;
        // this.undoStack = new UndoActionStack();
        // this.intersectedObject = null;
        // this.selectedObject = {};
        // this.mode = 'Model';
        // this.gltfLoadedParts = '';
        // this.gltfLoadedPercentual = 0;
        // this.stopAnimate();
        // this.loadCounter = 0;
    }

    public addToScene(group: THREE.Group): void {
        this.scene.add(group);
        this.requestRender();
    }

    public addToCssScene(group: THREE.Group): void {
        this.scenecss.add(group);
        this.requestRender();
    }

    public outlineElement(surfaces: THREE.Object3D[]) {
      this.selectionOutlinePass.setPrimary(surfaces);
      this.requestRender();
    }

    public outlineSecondaryElement(surfaces: THREE.Object3D[]) {
      // Pinned objects (the insertion pivot) survive every secondary re-outline;
      // setSecondary already removes the surfaces from the primary channel.
      this.selectionOutlinePass.setSecondary(
        Array.from(new Set([...this.pinnedSecondaryOutlineObjects, ...surfaces])));
      this.requestRender();
    }

    private pinnedSecondaryOutlineObjects: THREE.Object3D[] = []

    /** Outline that survives clearOutlinedElements/outlineSecondaryElement — used for the insertion pivot. */
    public pinSecondaryOutline(surfaces: THREE.Object3D[]) {
      this.pinnedSecondaryOutlineObjects = surfaces
      this.outlineSecondaryElement(surfaces)
    }

    public clearPinnedSecondaryOutline() {
      // Also drop the pinned objects from the pass itself: many mode-exit paths call
      // this without a follow-up clearOutlinedElements, and the outline must not
      // stay on screen after the pin is gone.
      const pinned = this.pinnedSecondaryOutlineObjects
      this.pinnedSecondaryOutlineObjects = []
      if (pinned.length > 0) {
        for (const obj of pinned) this.selectionOutlinePass.removeSecondary(obj);
        this.requestRender();
      }
    }

    public addOutlineElement(surface: THREE.Object3D) {
      this.selectionOutlinePass.addPrimary(surface);
      this.requestRender();
    }

    public addSecondaryOutlineElement(surface: THREE.Object3D) {
      this.selectionOutlinePass.addSecondary(surface);
      this.requestRender();
    }

    public clearOutlinedElements(alsoSecondary: boolean = true) {
      this.selectionOutlinePass.clearOutlines(alsoSecondary);
      // The pinned pivot outline survives a full clear.
      if (alsoSecondary && this.pinnedSecondaryOutlineObjects.length > 0) {
        this.selectionOutlinePass.setSecondary([...this.pinnedSecondaryOutlineObjects]);
      }
      this.requestRender();
    }

    /** Snapshot of the meshes currently outlined in the primary channel. */
    public getOutlinedElements(): THREE.Object3D[] {
      return this.selectionOutlinePass.getPrimaryObjects();
    }

    /** Remove a single object from the primary outline channel. */
    public removeOutlineElement(surface: THREE.Object3D) {
      this.selectionOutlinePass.removePrimary(surface);
      this.requestRender();
    }
}
